import { useState, useCallback, useEffect } from 'react';
import { NavigationItem, ChatHistoryGroup, NavigationState } from '@/types/navigation';
import { useChatInterface } from '@/contexts/ChatInterfaceContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FileAttachment } from '@/types/posit';

export const useSidebarNavigation = () => {
  const { currentPosit, clearMessages, setCurrentPosit } = useChatInterface();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [navigationState, setNavigationState] = useState<NavigationState>({
    activeSection: 'chat',
    searchQuery: '',
    historyFilter: 'today',
    recentFiles: [],
    chatHistory: []
  });

  // File preview modal state
  const [selectedFile, setSelectedFile] = useState<FileAttachment | null>(null);
  const [showFilePreview, setShowFilePreview] = useState(false);

  // Query for recent files from user's Posit attachments
  const { data: recentFilesData } = useQuery({
    queryKey: ['recent-files', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('posit_attachments')
        .select('file_id, file_name, file_type, source, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) {
        return [];
      }

      return data.map(file => ({
        id: file.file_id,
        label: file.file_name,
        badge: file.source === 'google_drive' ? 'Drive' : getFileTypeBadge(file.file_type),
        icon: undefined
      } as NavigationItem));
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Query for real chat history
  const { data: chatHistoryData } = useQuery({
    queryKey: ['chat-history', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Try to get chat history from chat_history table first, then fall back to chat_sessions
       let { data: historyData, error: historyError } = await supabase
        .from('chat_history')
         .select(`
          id,
          created_at,
          last_message_at,
          summary,
          posits!inner (
            id,
            title
          )
        `)
        .eq('user_id', user.id)
        .order('last_message_at', { ascending: false })
        .limit(20);

      if (historyError || !historyData || historyData.length === 0) {
        // Fallback to chat_messages if chat_history is empty
        // Only get messages from actual chats (posit_query source), not from posit updates
        const { data: messageData, error: messageError } = await supabase
          .from('chat_messages')
          .select(`
            posit_id,
            created_at,
            posits!inner (
              id,
              title
            )
          `)
          .eq('user_id', user.id)
          .eq('source', 'posit_query')
          .eq('role', 'user')
          .order('created_at', { ascending: false })
          .limit(20);

        if (messageError) {
          return [];
        }

        // Remove duplicates and get unique posits, then convert to history format
        const uniquePosits = messageData?.reduce((acc, message) => {
          if (!acc.find(item => item.posit_id === message.posit_id)) {
            acc.push(message);
          }
          return acc;
        }, [] as typeof messageData) || [];

        // Convert message data to history format
        historyData = uniquePosits.map(message => ({
          id: `message-${message.posit_id}`,
          created_at: message.created_at,
          last_message_at: message.created_at,
          summary: null,
          posits: message.posits
        })) || [];
      }

      if (!historyData || historyData.length === 0) {
        return [];
      }

      // Process and group by time periods
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);

      const groupedHistory: ChatHistoryGroup[] = [
        { date: 'today', label: 'Today', conversations: [] },
        { date: 'yesterday', label: 'Yesterday', conversations: [] },
        { date: 'week', label: 'This Week', conversations: [] },
        { date: 'older', label: 'Older', conversations: [] }
      ];

      // Process each history item and detect session type + count messages
      const conversationsWithTypes = await Promise.all(
        historyData.map(async (history) => {
          // Check if this is an update session by looking for update messages
          const { data: updateMessages } = await supabase
            .from('chat_messages')
            .select('source')
            .eq('chat_history_id', history.id)
            .eq('source', 'posit_update')
            .limit(1);
          
          const isUpdateSession = updateMessages && updateMessages.length > 0;
          
          // Count total messages in this conversation
          const { count: messageCount } = await supabase
            .from('chat_messages')
            .select('*', { count: 'exact', head: true })
            .eq('chat_history_id', history.id);
          
          // Compute a short summary (<= 5 words) from the stored summary as a fallback
          const shortFromSummary = (history.summary || '')
            .replace(/^[-•\s]+/g, '')
            .split(/\s+/)
            .slice(0, 5)
            .join(' ')
            .trim() || undefined;

          return {
            id: history.id,
            title: history.summary ? 
              `${history.posits.title}: ${history.summary.substring(0, 50)}...` :
              history.posits.title, // Just use the posit title directly
            last_message_at: history.last_message_at,
            posit_title: history.posits.title,
            posit_id: history.posits.id,
            message_count: messageCount || 0, // Now showing actual message count
            sessionType: isUpdateSession ? 'update' : 'chat' as 'update' | 'chat',
            short_summary: shortFromSummary
          };
        })
      );

      // Filter out conversations with zero messages
      const nonEmptyConversations = conversationsWithTypes.filter(c => (c.message_count || 0) > 0);

      // Group conversations by time periods
      nonEmptyConversations.forEach(conversation => {
        const sessionDate = new Date(conversation.last_message_at);
        
        if (sessionDate >= today) {
          groupedHistory[0].conversations.push(conversation);
        } else if (sessionDate >= yesterday) {
          groupedHistory[1].conversations.push(conversation);
        } else if (sessionDate >= weekAgo) {
          groupedHistory[2].conversations.push(conversation);
        } else {
          groupedHistory[3].conversations.push(conversation);
        }
      });

      // Filter out empty groups
      return groupedHistory.filter(group => group.conversations.length > 0);
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Helper function to get file type badge
  const getFileTypeBadge = (mimeType: string): string => {
    if (mimeType.includes('pdf')) return 'PDF';
    if (mimeType.includes('image')) return 'Image';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'Excel';
    if (mimeType.includes('document') || mimeType.includes('word')) return 'Word';
    if (mimeType.includes('text')) return 'Text';
    return 'File';
  };

  // Helper function to generate conversation titles
  const generateConversationTitle = (positTitle: string, messageCount: number): string => {
    const conversationStarters = [
      positTitle, // Just use the posit title directly
      `Questions about ${positTitle}`,
      `${positTitle} discussion`,
      `Exploring ${positTitle}`,
      `${positTitle} insights`
    ];
    
    // Use a simple hash to consistently pick the same title for similar sessions
    const hash = positTitle.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    const index = Math.abs(hash) % conversationStarters.length;
    return `${conversationStarters[index]} (${messageCount} messages)`;
  };

  // Update state when data changes
  useEffect(() => {
    setNavigationState(prev => ({
      ...prev,
      recentFiles: recentFilesData || [],
      chatHistory: chatHistoryData || []
    }));
  }, [recentFilesData, chatHistoryData]);

  const startNewChat = useCallback(() => {
    clearMessages();
    setNavigationState(prev => ({
      ...prev,
      activeSection: 'chat'
    }));
  }, [clearMessages]);

  const selectConversation = useCallback(async (conversationId: string) => {
    try {
      
      // Try chat_history first
      let { data: historyData, error: historyError } = await supabase
        .from('chat_history')
        .select(`
          id,
          posit_id,
          posits!inner (
            id,
            title
          )
        `)
        .eq('id', conversationId)
        .single();

      if (historyError || !historyData) {
        // Fallback to chat_sessions
        const { data: sessionData, error: sessionError } = await supabase
          .from('chat_sessions')
          .select(`
            id,
            posit_id,
            session_type,
            posits!inner (
              id,
              title,
              user_id
            )
          `)
          .eq('id', conversationId)
          .single();

        if (sessionError) {
          return;
        }

        historyData = {
          id: sessionData.id,
          posit_id: sessionData.posit_id,
          posits: sessionData.posits
        };
      }

      // Determine conversation type to route correctly
      const { data: updateMessages } = await supabase
        .from('chat_messages')
        .select('source')
        .eq('chat_history_id', conversationId)
        .eq('source', 'posit_update')
        .limit(1);
      
      const isUpdateSession = updateMessages && updateMessages.length > 0;
      const positId = historyData.posit_id;

      if (isUpdateSession) {
        // Route update sessions to the update interface
        navigate(`/update-posit/${positId}?conversation=${conversationId}`);
      } else {
        // Route Q&A sessions to the chat interface  
        navigate(`/chat/${positId}/${conversationId}`);
      }
      
    } catch (error) {
    }
  }, [navigate]);

  const selectFile = useCallback(async (fileId: string) => {
    try {
      
      // Get complete file details from posit_attachments including URL
      // Use order by created_at DESC and limit 1 to handle duplicates
      const { data: fileDataArray, error } = await supabase
        .from('posit_attachments')
        .select('file_id, file_name, file_type, posit_id, source, user_id, file_url')
        .eq('file_id', fileId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        return;
      }

      if (!fileDataArray || fileDataArray.length === 0) {
        return;
      }

      const fileData = fileDataArray[0]; // Get the most recent entry

      // Create signed URL for preview if we stored a storage path
      let signedUrl: string | undefined = undefined;
      try {
        if (fileData.file_url && !/^https?:\/\//i.test(fileData.file_url)) {
          const { data: signed, error: signError } = await supabase
            .storage
            .from('posit-attachments')
            .createSignedUrl(fileData.file_url, 600);
          if (!signError && signed?.signedUrl) {
            signedUrl = signed.signedUrl;
          }
        } else {
          signedUrl = fileData.file_url;
        }
      } catch (_) {}

      // Create FileAttachment object for the preview modal
      const fileAttachment: FileAttachment = {
        id: fileData.file_id,
        name: fileData.file_name,
        mimeType: fileData.file_type,
        source: fileData.source as 'upload' | 'google_drive',
        url: signedUrl,
        isInitialFile: true
      };

      // Set the selected file and show preview modal
      setSelectedFile(fileAttachment);
      setShowFilePreview(true);
      
    } catch (error) {
    }
  }, []);

  const setActiveSection = useCallback((section: string) => {
    setNavigationState(prev => ({
      ...prev,
      activeSection: section
    }));
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    setNavigationState(prev => ({
      ...prev,
      searchQuery: query
    }));
  }, []);

  const setHistoryFilter = useCallback((filter: 'today' | 'yesterday' | 'week' | 'older') => {
    setNavigationState(prev => ({
      ...prev,
      historyFilter: filter
    }));
  }, []);

  // File preview modal functions
  const closeFilePreview = useCallback(() => {
    setShowFilePreview(false);
    setSelectedFile(null);
  }, []);

  // Filter chat history based on current filter
  const filteredChatHistory = (navigationState.chatHistory || []).filter(group => {
    if (navigationState.historyFilter === 'today') return group.date === 'today';
    if (navigationState.historyFilter === 'yesterday') return group.date === 'yesterday';
    if (navigationState.historyFilter === 'week') return ['today', 'yesterday', 'week'].includes(group.date);
    return true; // 'older' shows all
  });

  return {
    ...navigationState,
    filteredChatHistory,
    startNewChat,
    selectConversation,
    selectFile,
    setActiveSection,
    setSearchQuery,
    setHistoryFilter,
    currentPosit,
    // File preview modal state and functions
    selectedFile,
    showFilePreview,
    closeFilePreview
  };
}; 